import React, { useState, useEffect, useRef } from "react";
import { FaRobot } from "react-icons/fa";
import "./ChatBot.css";

const responses = {
  hi: "Hello! 👋 Welcome to Covenants Developers. Looking to invest or find your dream home?",
  hello: "Hi there! 🏡 Need help with luxury properties, investments, or real estate queries?",

  services: `🏗️ What we offer:

1. 🏡 Real Estate Development – From Simplex to Villas
2. 💼 Investment Advisory – Smart property investment guidance
3. 🛠️ Property Management – Reliable & professional support
4. 🧱 Project Consultation – Architecture, planning & execution

Type 'read more' for in-depth details.`,

  projects: `🏢 Our Projects Include:
- Simplex
- Duplex
- Triplex
- Villa
All built with premium architecture & amenities. Type 'residential' or 'luxury' for specifics, or visit our website for full listings.`,

  "residential projects":
    "🏘️ We offer a variety of residential projects including simplex, duplex, triplex, and luxurious villas designed for modern living.",

  "luxury projects":
    "✨ Our luxury projects redefine elegance — premium finishes, cutting-edge architecture, lush green surroundings, and top-tier amenities.",

  about: `🏠 About Us:
At Covenants Developers, we build more than homes — we build trust. 

With 5+ projects completed and more coming soon, our team is dedicated to creating innovative, high-quality real estate solutions that reflect your lifestyle.

We deliver:
• Luxury Housing Projects
• Investment-Grade Properties
• Modern Architecture & Planning
• Trust-driven Service`,

  blog: `📝 Latest Blogs:
1. Why Invest in Luxury Real Estate?
2. Future-Proof Your Property Investments
3. Exploring Urban Housing Trends

Type the blog name or 'read more' to get a summary.`,

  "investment corner": `📈 Investment Corner:
Looking to grow your portfolio? We offer:
• Expert investment guidance
• Prime location properties
• Transparent dealings
• Long-term value

Type 'read more' or visit the Investment Corner page.`,

  contact: `📞 Contact Us:
• Address: Plot-1894 and 1895/9972, Shankarpur, Beside Tata Ariana, Bhubaneswar - 751003
• Phone: 09937003507
• Email: covenantsdevelopers@gmail.com`,

  "read more":
    "🌐 Explore more by visiting our website. Browse through our projects, blogs, and services to find everything you need to make an informed decision.",

  default:
    "🤖 Sorry, I didn't catch that. You can ask about 'services', 'projects', 'investment corner', 'about', or 'contact'.",
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible((prev) => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, typing]);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  const toggleChat = () => setIsOpen(!isOpen);

  const handleStart = () => {
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!phoneRegex.test(phone.trim())) {
      setErrorMsg("📱 Please enter a valid 10-digit Indian phone number.");
      return;
    }

    setErrorMsg("");
    setMessages([
      {
        sender: "bot",
        text: `Thanks ${name || "there"}! Let's get started. Ask me anything about construction 🏗️ or type 'hi'`,
      },
    ]);
    setStep(1);
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    const userInput = input.toLowerCase();

    let botText = responses[userInput] || null;

    // Fuzzy keyword match
    if (!botText) {
      if (userInput.includes("service")) botText = responses["services"];
      else if (userInput.includes("project")) botText = responses["projects"];
      else if (userInput.includes("residential"))
        botText = responses["residential projects"];
      else if (userInput.includes("commercial"))
        botText = responses["commercial projects"];
      else if (userInput.includes("about")) botText = responses["about"];
      else if (userInput.includes("blog")) botText = responses["blog"];
      else if (
        userInput.includes("contact") ||
        userInput.includes("phone") ||
        userInput.includes("address") ||
        userInput.includes("email")
      )
        botText = responses["contact"];
      else if (userInput.includes("read more"))
        botText = responses["read more"];
      else botText = responses["default"];
    }

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setTyping(true);

    setTimeout(() => {
      setMessages((prev) => [...prev, { sender: "bot", text: botText }]);
      setTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (step === 0) {
        handleStart();
      } else {
        handleSend();
      }
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <div className="chatbox animated-popup">
          <div className="floating-particles">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 6}s`,
                  animationDuration: `${4 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>

          <div className="chat-header">Developer Bot Pro</div>

          <div ref={bottomRef} className={`chat-body ${step === 0 ? "centered" : "scrolled"}`}>
            {step === 0 ? (
              <div className="onboarding-form">
                <p>
                  👋 Welcome to Developer Bot Pro! <br />
                  Let's build something amazing together!
                </p>
                <input
                  type="tel"
                  placeholder="📱 Your Phone Number *"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={handleKeyPress}
                />
                <input
                  type="text"
                  placeholder="👤 Your Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={handleKeyPress}
                />
                {errorMsg && <div className="chat-error-msg">{errorMsg}</div>}
                <button onClick={handleStart}>🚀 Start Building</button>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <div key={i} className={`chat-msg ${msg.sender}`}>
                    <span>{msg.text}</span>
                  </div>
                ))}
                {typing && (
                  <div className="chat-msg bot typing">
                    <div className="typing-indicator">
                      <span>Bot is typing</span>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </>
            )}
          </div>

          {step === 1 && (
            <div className="chat-input">
              <input
                type="text"
                value={input}
                placeholder="Ask about our services..."
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button onClick={handleSend}>Send ✨</button>
            </div>
          )}
        </div>
      )}

      <button
        className={`chat-toggle-btn group ${isOpen ? "open" : ""}`}
        onClick={toggleChat}
      >
        <div className="icon-wrapper">
          {isOpen ? (
            <span className="close-icon">✕</span>
          ) : (
            <span className="robot-icon"><FaRobot size={isMobile ? 20 : 22}/></span>
          )}
        </div>
        {!isOpen && isVisible && <div className="notification-dot bounce">!</div>}
      </button>
    </div>
  );
};

export default ChatBot;